import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLeftBar } from "../../context/LeftBar";
import NavItem from "./MenuItem/NavItem";
import { styled } from "@mui/material/styles";
import { IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DashboardIcon from "@mui/icons-material/Dashboard";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";

import { ReactComponent as IconInsights } from "../../assets/icons/sideMenu/iconInsights.svg";

import styles from "../LeftSideMenu/styles.css";

const StyledSidebar = styled("div")(({ theme, isOpen }) => ({
  width: 240,
  backgroundColor: "#fff",
  height: "calc(100vh - 64px)",
  position: "fixed",
  left: 0,
  top: 64,
  zIndex: 1,
  borderRight: "1px solid #eee",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "&.collapsed": {
    width: 65,
    paddingRight: 0,
    "& .navItem": {
      width: "40px",
      margin: "2px auto",
      "& a": {
        justifyContent: "center",
        padding: 0,
      },
      "& a:hover": {
        borderRadius: "8px",
      },
    },
    "& .leftSideHeader": {
      justifyContent: "center",
      padding: 0,
      "& .MuiButtonBase-root": {
        margin: "8px 0",
      },
    },
    "& .leftSideContentTitle": {
      padding: "16px 0",
      margin: "0 auto",
      width: "32px",
      borderTop: "1px solid #eee",
      fontSize: 0,
    },
    "& .navItemNameContainer": {
      justifyContent: "center",
      padding: "8px 0",
      width: "32px",
      margin: "0 auto",
      "& svg": {
        width: 20,
        height: 20,
      },
    },
    "& a.active": {
      backgroundColor: "#00843D",
      borderRadius: "8px",
      width: "40px",
      marginRight: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": {
        position: "relative",
        zIndex: 1,
        color: "#fff",
      },
    },
    "& .navItemName": {
      display: "none",
    },
  },
  "& .leftSideHeader": {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 8px",
    "& .MuiButtonBase-root": {
      padding: 8,
      margin: "8px 0",
    },
  },
  "& .leftSideContent": {
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
  "& .leftSideContentTitle": {
    fontSize: "11px",
    color: "#666",
    padding: "24px 24px 8px",
    margin: 0,
  },
  "& .navItemNameContainer": {
    padding: "8px 0",
    display: "flex",
    alignItems: "center",
    "& svg": {
      width: 18,
      height: 18,
      color: "#666",
      marginRight: isOpen ? "12px" : 0,
    },
  },
  "& a": {
    textDecoration: "none",
    color: "#333",
    display: "block",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "&.active": {
      backgroundColor: "#00843D",
      color: "#fff",
      borderRadius: "0 8px 8px 0",
      "& svg": {
        color: "#fff",
      },
    },
  },
  "& .navItem": {
    width: "94%",
    "& a": {
      justifyContent: "flex-start",
      paddingLeft: "24px",
    },
    "& a:hover": {
      borderRadius: "0 8px 8px 0",
    },
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

function LeftSideMenu({ app, config, logo }) {
  const { leftBarOpened, setLeftBarOpened } = useLeftBar();

  const [seeInsights, setSeeInsights] = useState("");
  const [seeApuracao, setSeeApuracao] = useState("");
  const [seeRankingGeral, setSeeRankingGeral] = useState("");

  const getNameUser = useCallback(
    async (objectId, screen) => {
      if (app && objectId) {
        try {
          const res = await app.visualization.get(objectId);
          const value =
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qText ??
            res?.model?.layout?.qHyperCube?.qGrandTotalRow[0]?.qNum ??
            null;

          screen === "insights" && setSeeInsights(value === "1" ? true : false);

          screen === "apuracao" && setSeeApuracao(value === "1" ? true : false);

          screen === "rankingGeral" && setSeeRankingGeral(value === "1" ? true : false);
        } catch (error) {
          console.error("Erro ao buscar nome do usuário:", error);
        }
      }
    },
    [app]
  );

  useEffect(() => {
    if (app) {
      getNameUser("jJKsq", "insights");
      getNameUser("gPXpay", "apuracao");
      getNameUser("TPdqXS", "rankingGeral");
    }
  }, [app, getNameUser]);

  if (!app || !config) {
    return null;
  }

  return (
    <StyledSidebar
      className={`leftSideMenu ${!leftBarOpened ? "collapsed" : "is-opened"}`}
      isOpen={leftBarOpened}
    >
      <div className="leftSideHeader">
        <IconButton onClick={() => setLeftBarOpened(!leftBarOpened)}>
          {leftBarOpened ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </div>

      <div className="leftSideContent">
        <ul>
          <NavItem
            muiIcon={<DashboardIcon />}
            name="Página Inicial"
            path="/overview"
          />
        </ul>

        <h3 className="leftSideContentTitle">PROGRAMA DE EXCELÊNCIA</h3>
        <ul>
          <NavItem
            muiIcon={<CurrencyExchangeIcon />}
            name="Positivação Total"
            path="/positivity-total"
          />
        </ul>
        <ul>
          <NavItem
            muiIcon={<QueryStatsIcon />}
            name="Detalhamento"
            path="/details"
          />
        </ul>

        {seeApuracao && (
          <ul>
            <NavItem
              muiIcon={<TrendingUpIcon />}
              name="Apuração"
              path="/investigation"
            />
          </ul>
        )}

        {seeInsights && (
          <ul>
            <NavItem
              muiIcon={<IconInsights />}
              name="Insights"
              path="/insights"
            />
          </ul>
        )}

        {seeRankingGeral && (
          <ul>
            <NavItem
              muiIcon={<LeaderboardIcon />}
              name="Ranking Geral"
              path="/ranking-geral"
            />
          </ul>
        )}

        <h3 className="leftSideContentTitle">AJUDA</h3>
        <ul>
          <NavItem
            muiIcon={<DescriptionOutlinedIcon />}
            name="Regras da Campanha"
            path="/roles"
          />
        </ul>
        <ul>
          <NavItem
            muiIcon={<ContactSupportOutlinedIcon />}
            name="Perguntas Frequentes"
            path="/faq"
          />
        </ul>
      </div>
    </StyledSidebar>
  );
}

LeftSideMenu.defaultProps = {
  app: null,
  config: null,
  logo: null,
};

LeftSideMenu.propTypes = {
  app: PropTypes.object,
  config: PropTypes.object,
  logo: PropTypes.object,
};

export default LeftSideMenu;
